import React, {
    createContext, memo, useContext, useEffect, useState, useMemo, useCallback,
} from 'react'
import { useIsDrawingNumbers } from './BlockPotProvider'
import useRoundDraw from '@/hooks/contracts/lottery/useRoundDraw'
import useChainChanged from '@/hooks/web3/useChainChanged'
import { LotteryEntry, LotteryRound } from '@/types/lottery'
import useFiatConverter from '@/hooks/utilities/useFiatConverter'
import { useAccount } from 'wagmi'
import useNativeCurrency from '@/hooks/web3/useNativeCurrency'
import { ZERO_ADDRESS } from '@/web3/constants'
import { DisplayDrawnNumberData } from '@/types/lottery/display-drawn-number-data'
import { createDisplayDrawnNumberData } from '@/utilities/lottery/display-drawn-number-data'
import DrawSummaryDialog from '@/components/blockpot/modals/DrawSummaryDialog/DrawSummaryDialog'
import usePlayerEntries from '@/hooks/contracts/lottery/usePlayerEntries'
import { useDrawSummaryDialogOpen, usePreviousRoundsPanelOpen } from './ModalOpenStateProvider'
import { useMissedDraw } from './MissedDrawProvider'

export type LotteryDrawContext = {
    roundIndex: number
    drawStage: LotteryDrawStage
}

export type StagedDraw = {
    drawnNumbers: DisplayDrawnNumberData[]
}

type LotteryDrawWaitingStage = { type: 'waiting', roundIndex: number }
type LotteryDrawDrawingStage = { type: 'drawing', drawnRound: LotteryRound, stagedDraw: StagedDraw, playerEntries: LotteryEntry[] }
type LotteryDrawCompleteStage = { type: 'complete', drawnRound: LotteryRound, stagedDraw: StagedDraw, playerEntries: LotteryEntry[] }
export type LotteryDrawStage = LotteryDrawWaitingStage | LotteryDrawDrawingStage | LotteryDrawCompleteStage

type BlockPotDrawContextType = {
    draw: LotteryDrawContext | undefined
    clearDraw: () => void
    setDraw: (draw: LotteryDrawContext | undefined) => void
    drawnRound: LotteryRound | undefined
    viewRoundSummary: (round: LotteryRound) => void
    replayDraw: (roundIndex: number) => void
    advanceDraw: () => void
}

const BlockPotDrawContext = createContext<BlockPotDrawContextType | undefined>(undefined)

type Props = {
    children: React.ReactNode
}

function BlockPotDrawProvider({ children }: Props): React.ReactElement {
    const chainChanged = useChainChanged()
    const { address } = useAccount()
    const nativeToken = useNativeCurrency()
    const fiatConverter = useFiatConverter()
    const isDrawingNumbers = useIsDrawingNumbers()
    const [drawnRoundIndexOverride, setDrawnRoundIndexOverride] = useState<number | undefined>()
    const { roundIndex: roundDrawRoundIndex, drawnRound, clearDrawnRound } = useRoundDraw(chainChanged, drawnRoundIndexOverride)
    const [drawnRoundSummary, setDrawnRoundSummary] = useState<LotteryRound | undefined>()
    const playerEntries = usePlayerEntries(drawnRound?.roundIndex ?? -1)
    const [draw, setDraw] = useState<LotteryDrawContext | undefined>()
    const drawSummaryDialogOpen = useDrawSummaryDialogOpen()
    const previousRoundsPanelOpen = usePreviousRoundsPanelOpen()
    const { markRoundAsSeen } = useMissedDraw()

    const displayDrawnNumberData = useMemo(() => {
        if (!drawnRound) return []
        return createDisplayDrawnNumberData(
            [...drawnRound.draws],
            address ?? ZERO_ADDRESS,
            nativeToken,
            fiatConverter
        )
    }, [drawnRound, address, nativeToken, fiatConverter])

    const roundIndex = drawnRound?.roundIndex ?? roundDrawRoundIndex

    const advanceDraw = useCallback(() => {
        if (!draw || draw.drawStage.type !== 'drawing') { return }
        const draws = draw.drawStage.drawnRound.draws

        const currentDrawnNumbersLength = draw.drawStage.stagedDraw.drawnNumbers.length
        if (currentDrawnNumbersLength >= draws.length) {
            setDraw((draw: LotteryDrawContext | undefined) => {
                if (!draw || draw.drawStage.type !== 'drawing') { return draw }
                const drawCopy = { ...draw }
                drawCopy.drawStage.type = 'complete'
                return drawCopy
            })
            drawSummaryDialogOpen.update(true)
        } else {
            setDraw((draw: LotteryDrawContext | undefined) => {
                if (!draw || draw.drawStage.type !== 'drawing') { return draw }
                return {
                    roundIndex,
                    drawStage: {
                        type: 'drawing',
                        drawnRound: draw.drawStage.drawnRound,
                        stagedDraw: {
                            drawnNumbers: displayDrawnNumberData.slice(0, currentDrawnNumbersLength + 1)
                        },
                        playerEntries: playerEntries?.entries ?? []
                    }
                }
            })
        }
    }, [draw, roundIndex, displayDrawnNumberData, playerEntries?.entries, drawSummaryDialogOpen])

    const clearDraw = () => {
        setDraw(undefined)
        clearDrawnRound()
    }

    useEffect(() => {
        if ((isDrawingNumbers || drawnRound) && !draw) {
            setDraw({
                roundIndex,
                drawStage: { type: 'waiting', roundIndex }
            })
            // Mark round as seen when watching live draw
            markRoundAsSeen(roundIndex)
        } else if (draw && draw.drawStage.type === 'waiting' && drawnRound) {
            const cancel = setTimeout(() => {
                setDraw({
                    roundIndex,
                    drawStage: {
                        type: 'drawing',
                        drawnRound,
                        stagedDraw: {
                            drawnNumbers: displayDrawnNumberData.slice(0, 1)
                        },
                        playerEntries: playerEntries?.entries ?? []
                    }
                })
            }, 3000)
            return () => clearTimeout(cancel)
        }
    }, [isDrawingNumbers, draw, roundIndex, drawnRound, displayDrawnNumberData, playerEntries?.entries, markRoundAsSeen])

    const viewRoundSummary = (round: LotteryRound) => {
        setDrawnRoundSummary(round)
        drawSummaryDialogOpen.update(true)
    }

    const replayDraw = (roundIndex: number) => {
        setDrawnRoundIndexOverride(roundIndex)
        drawSummaryDialogOpen.update(false)
        previousRoundsPanelOpen.update(false)
    }

    const blockPotDrawContextValue: BlockPotDrawContextType = {
        draw,
        clearDraw,
        setDraw,
        drawnRound,
        viewRoundSummary,
        replayDraw,
        advanceDraw
    }

    const roundIndexToView = drawnRoundSummary?.roundIndex ?? drawnRound?.roundIndex

    return (
        <BlockPotDrawContext.Provider value={blockPotDrawContextValue}>
            {children}
            {
                (roundIndexToView !== undefined && roundIndexToView !== null) && (
                    <DrawSummaryDialog
                        open={drawSummaryDialogOpen.value}
                        onClose={() => {
                            setDrawnRoundIndexOverride(undefined)
                            drawSummaryDialogOpen.update(false)
                            clearDraw()
                        }}
                        roundIndex={roundIndexToView ?? -1}
                        onReplayDraw={(roundIndex) => {
                            setDrawnRoundIndexOverride(roundIndex)
                            drawSummaryDialogOpen.update(false)
                            previousRoundsPanelOpen.update(false)
                        }}
                    />
                )
            }
        </BlockPotDrawContext.Provider>
    )
}

export const useLotteryDraw = () => {
    const context = useContext(BlockPotDrawContext)
    if (context === undefined) {
        throw new Error('useLotteryDraw must be used within a BlockPotDrawProvider')
    }
    return context
}

export default memo(BlockPotDrawProvider)
